import { useEffect, useState } from 'react';
import { Button, Input, Typography, message, Modal, Form, Select } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { Document } from 'app/interfaces/Document';
import './RequestSignature.scss';
import LoadingSpinner from 'app/components/common/LoadingSpinner';
import { DocusealBuilder } from '@docuseal/react';
import { DocusealService } from 'app/services/integrations/docuseal';
import { SIGNING_ORDERS } from 'app/configs/signature';

const { Title } = Typography;

interface LocationState {
  documents: Document[];
}

interface Recipient {
  id: string;
  role: string;
  name: string;
  email: string;
  uuid?: string;
  displayRole: string;
  sequence?: string | number;
  signingOrder: string;
}

const RequestSignature = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const documents = (location.state as LocationState)?.documents || [];

  const [token, setToken] = useState<string | undefined>();
  const [docusealTemplate, setDocusealTemplate] = useState<any>({});

  // Modal for submitters (signatories)
  const [showSubmitterModal, setShowSubmitterModal] = useState(false);
  const [signingOrder, setSigningOrder] = useState<string>(
    SIGNING_ORDERS.PRESERVED
  );

  const [initialSignatories, setInitialSignatories] = useState<any[]>([]);
  const [signatories, setSignatories] = useState<Recipient[]>([]);

  // Progress and result modals
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSignatoryChange = (idx: number, field: string, value: string) => {
    setSignatories((prev: any) =>
      prev.map((s: any, i: number) =>
        i === idx ? { ...s, [field]: value } : s
      )
    );
  };

  const handleSubmitterModalOk = async () => {
    if (signatories.some((s: any) => !s.name || !s.email)) {
      message.error('Please fill in all signatory names and emails');
      return;
    }

    // Close submitter modal and show progress modal
    setShowSubmitterModal(false);
    setShowProgressModal(true);

    try {
      // Make the backend call to send the signature request
      const response = await DocusealService.sendSignatureRequest({
        docusealTemplateId: docusealTemplate.id,
        recipients: signatories,
        documentIds: documents.map((doc) => doc._id),
        signingOrder: signingOrder,
      });

      console.log('Signature request sent successfully:', response);

      // Show success modal
      setShowProgressModal(false);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error sending signature request:', error);
      const errorMsg = error.message || 'Failed to send signature request';

      // Close progress modal and show submitter modal again with error
      setShowProgressModal(false);
      setShowSubmitterModal(true);
      message.error(errorMsg);
    }
  };

  const handleSubmitterModalCancel = () => {
    setShowSubmitterModal(false);
  };

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const documentIds = documents.map((doc) => doc._id).join(',');

        await DocusealService.getRequestSignatureToken(documentIds)
          .then((res) => {
            console.log('Request signature token fetched:', res.data);
            setToken(res.data.token);
            setDocusealTemplate(res.data.docusealTemplate);
            setSigningOrder(res.data?.signingOrder || SIGNING_ORDERS.PRESERVED);
            setInitialSignatories(res.data?.signatories || []);
            setSignatories(res.data?.signatories || []);
          })
          .catch((error) => {
            console.error('Error fetching request signature token:', error);
            const errorMsg =
              error?.response?.data?.message ||
              error?.message ||
              'Failed to fetch request signature token';
            setErrorMessage(errorMsg);
            setShowErrorModal(true);
          });
      } catch (error: any) {
        console.error('Error fetching request signature token:', error);
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch request signature token';
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
      }
    };

    if (documents.length > 0) {
      fetchToken();
    }
  }, [documents]);

  const handleSuccessModalOk = () => {
    setShowSuccessModal(false);
    // Navigate back to transaction documents view
    navigate(-1);
  };

  const handleErrorModalOk = () => {
    setShowErrorModal(false);
    // Navigate back to the previous page
    navigate(-1);
  };

  const component = (
    <div className="request-signature">
      {/* Signatories and Signing Order Modal */}
      <Modal
        title={<div className="text-2xl font-medium mb-4">Signatories</div>}
        open={showSubmitterModal}
        onOk={handleSubmitterModalOk}
        onCancel={handleSubmitterModalCancel}
        okText="Confirm"
        cancelText="Cancel"
        width={600}
      >
        <Form layout="vertical">
          {signatories.map((signatory: any, idx) => (
            <div
              key={signatory.role}
              className="mb-5 p-4 border border-gray-300 rounded-lg bg-gray-50 box-border"
            >
              <div className="text-sm font-medium mb-2">
                {signatory.displayRole || `Signatory ${idx + 1}`}
              </div>
              <div className="flex flex-row gap-3 w-full">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-600 mb-1">Name</div>
                  <Input
                    value={signatory.name}
                    onChange={(e) =>
                      handleSignatoryChange(idx, 'name', e.target.value)
                    }
                    placeholder="Enter name"
                    className="w-full box-border"
                    style={{ width: 'calc(100% - 1.5rem)' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-600 mb-1">Email</div>
                  <Input
                    value={signatory.email}
                    onChange={(e) =>
                      handleSignatoryChange(idx, 'email', e.target.value)
                    }
                    placeholder="Enter email"
                    type="email"
                    className="w-full box-border"
                    style={{ width: 'calc(100% - 1.5rem)' }}
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Signing Order Selection */}
          <div className="mt-6 p-4 border border-gray-300 rounded-lg bg-gray-50 box-border">
            <div className="text-sm font-medium mb-2">Signing Order</div>
            <Select
              value={signingOrder}
              onChange={setSigningOrder}
              className="w-full"
              placeholder="Select signing order"
            >
              <Select.Option value="preserved">
                Preserved (Sequential)
              </Select.Option>
              <Select.Option value="random">Random - (Parallel)</Select.Option>
            </Select>
          </div>
        </Form>
      </Modal>

      {/* Progress Loader Modal */}
      <Modal open={showProgressModal} footer={null} closable={false}>
        <div className="text-center py-5 px-2">
          <div className="text-sm mt-3">
            <div className="w-full mb-4">
              Sending signature request to all signatories...
              <div
                className="h-1.5 bg-blue-600 rounded-full mt-4"
                style={{
                  animation: 'exponentialProgress 3s forwards',
                  transformOrigin: 'left center',
                }}
              />
              <style>{`
              @keyframes exponentialProgress {
                0% {
                width: 0%;
                opacity: 0.7;
                }
                20% { width: 10%; }
                40% { width: 25%; }
                60% { width: 50%; }
                80% { width: 80%; }
                95% { width: 95%; }
                100% {
                width: 98%;
                opacity: 1;
                }
              }
              `}</style>
            </div>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onOk={handleSuccessModalOk}
        footer={[
          <Button key="ok" type="primary" onClick={handleSuccessModalOk}>
            Okay
          </Button>,
        ]}
        closable={false}
        width={500}
      >
        <div className="text-center py-10 px-5">
          <div className="text-2xl font-semibold mb-2">
            Signature Request sent
          </div>
          <div className="text-base text-gray-600">
            The signature request has been successfully sent to all submitters.
          </div>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        open={showErrorModal}
        onOk={handleErrorModalOk}
        footer={[
          <Button key="ok" type="primary" onClick={handleErrorModalOk}>
            Go Back
          </Button>,
        ]}
        closable={false}
        width={500}
      >
        <div className="text-center py-10 px-5">
          <div className="text-2xl font-semibold mb-2 text-red-600">Error</div>
          <div className="text-base text-gray-600">{errorMessage}</div>
        </div>
      </Modal>

      {/* Header */}
      <div className="request-signature__header">
        <Title level={3}>Request Signature</Title>
        <div className="request-signature__actions">
          <Button onClick={() => navigate(-1)}>Return to Documents</Button>
          <Button type="primary" onClick={() => setShowSubmitterModal(true)}>
            Next
          </Button>
        </div>
      </div>

      {/* Docuseal Signature Template Editor Module */}
      {token ? (
        <div className="border rounded-lg bg-white">
          <DocusealBuilder
            token={token}
            customCss={`
              .save-button {
                background-color: #2F78EB !important;
                color: #ffffff;
                text-transform: none !important;
                border: none !important;
                font-size: 0.9rem !important;
                padding: 0.3rem 0.8rem !important;
                min-height: 32px !important;
                min-width: 100px !important;
              }
              
              .save-button:hover {
                background-color: #2468D1 !important;
              }
              
              .add-document-button {
                text-transform: none !important;
              }
              
              .add-document-button:hover {
                background-color: #2F78EB !important;
                color: #ffffff;
                text-transform: none !important;
                border: none !important;
                font-size: 0.9rem !important;
                padding: 0.3rem 0.8rem !important;
                min-height: 32px !important;
                min-width: 100px !important;
              }

              .template-name {
                font-size: 1.5rem;
              }

              .fields-list-item {
                display: none;
              }

              /* Custom scrollbar styles for material rounded look */
              ::-webkit-scrollbar {
                width: 10px;
                height: 10px;
                background: #f1f1f1;
                border-radius: 8px;
              }
              ::-webkit-scrollbar-thumb {
                background: #e0e0e0;
                border-radius: 8px;
                border: 2px solid #f1f1f1;
                transition: background 0.2s;
              }
              ::-webkit-scrollbar-thumb:hover {
                background: #bdbdbd;
              }
              ::-webkit-scrollbar-corner {
                background: #f1f1f1;
              }
              /* For Firefox */
              * {
                scrollbar-width: thin;
                scrollbar-color: #e0e0e0 #f1f1f1;
                border-radius: 8px;
              }
            `}
            style={{
              height: 'calc(100vh - 10.75rem)',
              padding: '1rem',
            }}
            withDocumentsList={true}
            withSignYourselfButton={false}
            withSendButton={false}
            previewMode={false}
            preview={false}
            autosave={true}
            onChange={(docusealTemplate: any) => {
              console.log('Docuseal template changed:', docusealTemplate);
              setDocusealTemplate(docusealTemplate);

              // Also propage the signatories to the template
              const templateSubmitters = docusealTemplate.submitters || [];
              console.log('Template submitters: ', templateSubmitters);

              // Filter signatories to only keep those that match template submitters
              const updatedSignatories = initialSignatories.filter((s: any) => {
                return templateSubmitters.some(
                  (sub: any) => sub.uuid === s.role
                );
              });

              console.log('Updated signatories: ', updatedSignatories);
              setSignatories(updatedSignatories);
              setInitialSignatories(updatedSignatories);
            }}
          />
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );

  return component;
};

export default RequestSignature;
